/**
 * Role-Based Access Control (RBAC) Utilities
 * Provides functions for checking user roles and permissions
 */

import { supabase } from '../lib/supabase';

// User roles in the system
export const ROLES = {
  CLIENT: 'client',
  LAWYER: 'lawyer',
  PARTNER: 'partner',
  ADMIN: 'admin'
};

// Role hierarchy (higher number = more permissions)
const ROLE_HIERARCHY = {
  [ROLES?.CLIENT]: 1,
  [ROLES?.LAWYER]: 2,
  [ROLES?.PARTNER]: 3,
  [ROLES?.ADMIN]: 4
};

// Resource permissions
export const RESOURCES = {
  USERS: 'users',
  CASES: 'cases',
  CLIENTS: 'clients',
  DOCUMENTS: 'documents',
  CONSULTATIONS: 'consultations',
  REPORTS: 'reports',
  SETTINGS: 'settings'
};

// Permission types
export const PERMISSIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete'
};

/**
 * Get current user's profile with role information
 * @returns {Promise<Object|null>} User profile or null
 */
export const getCurrentUserProfile = async () => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      return null;
    }

    const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError?.message);
      return null;
    }

    return profile;
  } catch (error) {
    console.error('Error in getCurrentUserProfile:', error?.message);
    return null;
  }
};

/**
 * Check if user has a specific role
 * @param {string} requiredRole - Role to check
 * @returns {Promise<boolean>}
 */
export const hasRole = async (requiredRole) => {
  try {
    const profile = await getCurrentUserProfile();
    return profile?.role === requiredRole && profile?.status === 'active';
  } catch (error) {
    console.error('Error in hasRole:', error?.message);
    return false;
  }
};

/**
 * Check if user has any of the specified roles
 * @param {string[]} requiredRoles - Array of roles to check
 * @returns {Promise<boolean>}
 */
export const hasAnyRole = async (requiredRoles) => {
  try {
    const profile = await getCurrentUserProfile();
    return requiredRoles?.includes(profile?.role) && profile?.status === 'active';
  } catch (error) {
    console.error('Error in hasAnyRole:', error?.message);
    return false;
  }
};

/**
 * Check if user's role is at least the specified level
 * @param {string} minimumRole - Minimum required role
 * @returns {Promise<boolean>}
 */
export const hasMinimumRole = async (minimumRole) => {
  try {
    const profile = await getCurrentUserProfile();
    if (!profile || profile?.status !== 'active') return false;
    
    const userLevel = ROLE_HIERARCHY?.[profile?.role] || 0;
    const requiredLevel = ROLE_HIERARCHY?.[minimumRole] || 0;
    
    return userLevel >= requiredLevel;
  } catch (error) {
    console.error('Error in hasMinimumRole:', error?.message);
    return false;
  }
};

/**
 * Check if user has permission for a specific resource and action
 * @param {string} resource - Resource name
 * @param {string} permission - Permission type (create, read, update, delete)
 * @returns {Promise<boolean>}
 */
export const hasPermission = async (resource, permission) => {
  try {
    const profile = await getCurrentUserProfile();
    if (!profile || profile?.status !== 'active') return false;

    const { data, error } = await supabase?.from('role_permissions')?.select('*')?.eq('role', profile?.role)?.eq('resource', resource)?.single();

    if (error) {
      console.error('Error fetching permissions:', error?.message);
      return false;
    }

    const permissionMap = {
      [PERMISSIONS?.CREATE]: data?.can_create,
      [PERMISSIONS?.READ]: data?.can_read,
      [PERMISSIONS?.UPDATE]: data?.can_update,
      [PERMISSIONS?.DELETE]: data?.can_delete
    };

    return permissionMap?.[permission] || false;
  } catch (error) {
    console.error('Error in hasPermission:', error?.message);
    return false;
  }
};

/**
 * Get all permissions for current user's role
 * @returns {Promise<Object>} Permissions object grouped by resource
 */
export const getUserPermissions = async () => {
  try {
    const profile = await getCurrentUserProfile();
    if (!profile || profile?.status !== 'active') return {};

    const { data, error } = await supabase?.from('role_permissions')?.select('*')?.eq('role', profile?.role);

    if (error) {
      console.error('Error fetching user permissions:', error?.message);
      return {};
    }

    // Convert array to object grouped by resource
    return data?.reduce((acc, perm) => {
      acc[perm.resource] = {
        create: perm?.can_create,
        read: perm?.can_read,
        update: perm?.can_update,
        delete: perm?.can_delete
      };
      return acc;
    }, {});
  } catch (error) {
    console.error('Error in getUserPermissions:', error?.message);
    return {};
  }
};

/**
 * Check if user is admin
 * @returns {Promise<boolean>}
 */
export const isAdmin = async () => {
  return hasRole(ROLES?.ADMIN);
};

/**
 * Check if user is partner or admin
 * @returns {Promise<boolean>}
 */
export const isPartnerOrAdmin = async () => {
  return hasAnyRole([ROLES?.PARTNER, ROLES?.ADMIN]);
};

/**
 * Check if user is lawyer, partner, or admin
 * @returns {Promise<boolean>}
 */
export const isLawyerOrAbove = async () => {
  return hasAnyRole([ROLES?.LAWYER, ROLES?.PARTNER, ROLES?.ADMIN]);
};

/**
 * Get role display name
 * @param {string} role - Role key
 * @returns {string} Display name
 */
export const getRoleDisplayName = (role) => {
  const displayNames = {
    [ROLES?.CLIENT]: 'Client',
    [ROLES?.LAWYER]: 'Lawyer',
    [ROLES?.PARTNER]: 'Partner',
    [ROLES?.ADMIN]: 'Administrator'
  };
  return displayNames?.[role] || role;
};

/**
 * Get default redirect path based on user role
 * @param {string} role - User role
 * @returns {string} Redirect path
 */
export const getDefaultRedirectPath = (role) => {
  const redirectPaths = {
    [ROLES?.CLIENT]: '/client-portal',
    [ROLES?.LAWYER]: '/dashboard',
    [ROLES?.PARTNER]: '/partner-portal',
    [ROLES?.ADMIN]: '/admin-dashboard'
  };
  return redirectPaths?.[role] || '/dashboard';
};

/**
 * Granular permission actions for component-level checks
 */
export const ACTIONS = {
  VIEW: 'view',
  EDIT: 'edit',
  DELETE: 'delete',
  APPROVE: 'approve',
  SUSPEND: 'suspend',
  ASSIGN: 'assign',
  EXPORT: 'export'
};

/**
 * Check if user can perform specific action on resource
 * @param {string} resource - Resource name
 * @param {string} action - Action type (view, edit, delete, approve, etc.)
 * @param {Object} context - Additional context (userId, ownerId, etc.)
 * @returns {Promise<boolean>}
 */
export const canPerformAction = async (resource, action, context = {}) => {
  try {
    const profile = await getCurrentUserProfile();
    if (!profile || profile?.status !== 'active') return false;

    // Admin can do everything
    if (profile?.role === ROLES?.ADMIN) return true;

    // Map actions to permission types
    const actionPermissionMap = {
      [ACTIONS?.VIEW]: PERMISSIONS?.READ,
      [ACTIONS?.EDIT]: PERMISSIONS?.UPDATE,
      [ACTIONS?.DELETE]: PERMISSIONS?.DELETE,
      [ACTIONS?.APPROVE]: PERMISSIONS?.UPDATE,
      [ACTIONS?.SUSPEND]: PERMISSIONS?.UPDATE,
      [ACTIONS?.ASSIGN]: PERMISSIONS?.UPDATE,
      [ACTIONS?.EXPORT]: PERMISSIONS?.READ
    };

    const permissionType = actionPermissionMap?.[action];
    if (!permissionType) return false;

    // Check base permission
    const hasBasePermission = await hasPermission(resource, permissionType);
    if (!hasBasePermission) return false;

    // Additional context-based checks
    if (context?.ownerId && context?.userId) {
      // User can only edit/delete their own resources (unless admin/partner)
      if ([ACTIONS?.EDIT, ACTIONS?.DELETE]?.includes(action)) {
        if (profile?.role === ROLES?.PARTNER) return true;
        return context?.ownerId === context?.userId;
      }
    }

    // Approve/Suspend actions require partner or admin role
    if ([ACTIONS?.APPROVE, ACTIONS?.SUSPEND]?.includes(action)) {
      return [ROLES?.PARTNER, ROLES?.ADMIN]?.includes(profile?.role);
    }

    return true;
  } catch (error) {
    console.error('Error in canPerformAction:', error?.message);
    return false;
  }
};

/**
 * Get user's allowed actions for a resource
 * @param {string} resource - Resource name
 * @param {Object} context - Additional context
 * @returns {Promise<string[]>} Array of allowed actions
 */
export const getAllowedActions = async (resource, context = {}) => {
  try {
    const profile = await getCurrentUserProfile();
    if (!profile || profile?.status !== 'active') return [];

    const allActions = Object.values(ACTIONS);
    const allowedActions = [];

    for (const action of allActions) {
      const canPerform = await canPerformAction(resource, action, context);
      if (canPerform) {
        allowedActions?.push(action);
      }
    }

    return allowedActions;
  } catch (error) {
    console.error('Error in getAllowedActions:', error?.message);
    return [];
  }
};

/**
 * Check if user can access admin features
 * @returns {Promise<boolean>}
 */
export const canAccessAdmin = async () => {
  return isAdmin();
};

/**
 * Check if user can manage users (approve, suspend, etc.)
 * @returns {Promise<boolean>}
 */
export const canManageUsers = async () => {
  return isAdmin();
};

/**
 * Check if user can view system analytics
 * @returns {Promise<boolean>}
 */
export const canViewAnalytics = async () => {
  return hasAnyRole([ROLES?.PARTNER, ROLES?.ADMIN]);
};

/**
 * Check if user can perform bulk operations
 * @returns {Promise<boolean>}
 */
export const canPerformBulkOperations = async () => {
  return hasAnyRole([ROLES?.PARTNER, ROLES?.ADMIN]);
};

/**
 * Synchronous role check using profile object (for component rendering)
 * @param {Object} profile - User profile object
 * @param {string} requiredRole - Required role
 * @returns {boolean}
 */
export const hasRoleSync = (profile, requiredRole) => {
  return profile?.role === requiredRole && profile?.status === 'active';
};

/**
 * Synchronous check for any role (for component rendering)
 * @param {Object} profile - User profile object
 * @param {string[]} requiredRoles - Array of required roles
 * @returns {boolean}
 */
export const hasAnyRoleSync = (profile, requiredRoles) => {
  return requiredRoles?.includes(profile?.role) && profile?.status === 'active';
};

/**
 * Check if action button should be visible based on permissions
 * @param {Object} profile - User profile
 * @param {string} action - Action type
 * @param {string} resource - Resource name
 * @returns {boolean}
 */
export const shouldShowAction = (profile, action, resource) => {
  if (!profile || profile?.status !== 'active') return false;
  
  // Admin sees all actions
  if (profile?.role === ROLES?.ADMIN) return true;
  
  // Partner sees most actions except some admin-only ones
  if (profile?.role === ROLES?.PARTNER) {
    return ![ACTIONS?.SUSPEND]?.includes(action);
  }
  
  // Lawyers see limited actions
  if (profile?.role === ROLES?.LAWYER) {
    return [ACTIONS?.VIEW, ACTIONS?.EDIT]?.includes(action);
  }
  
  // Clients see minimal actions
  return [ACTIONS?.VIEW]?.includes(action);
};